
import React, {
    useState,
    useEffect,
    useCallback
} from "react";

import {
    useNavigate,
    useParams,
    Link
} from "react-router-dom";

import { useAdminAuthContext } from "../admin/useAdminAuthContext";

import API_URL from "../../api/API";

import ProductIdentity from "./sections/ProductIdentity";
import ProductCustomerProblems from "./sections/ProductCustomerProblems";
import ProductApplications from "./sections/ProductApplications";
import ProductBenefits from "./sections/ProductBenefits";
import ProductSuitability from "./sections/ProductSuitability";
import ProductTechnicalSpecifications from "./sections/ProductTechnicalSpecifications";
import ProductApplicationMethod from "./sections/ProductApplicationMethod";
import ProductQualificationQuestions from "./sections/ProductQualificationQuestions";
import ProductFAQs from "./sections/ProductFAQs";
import ProductCaseStudies from "./sections/ProductCaseStudies";
import ProductSalesInformation from "./sections/ProductSalesInformation";
import ProductMedia from "./sections/ProductMedia";
import ProductApproval from "./sections/ProductApproval";

// ========================================
// PRODUCT SECTIONS
// ========================================

const PRODUCT_SECTIONS = [

    { key: "identity", label: "Product Identity" },

    { key: "customerProblems", label: "Customer Problems" },

    { key: "applications", label: "Applications" },

    { key: "benefits", label: "Benefits & Differentiators" },

    { key: "suitability", label: "Suitability & Limitations" },

    { key: "technicalSpecifications", label: "Technical Specifications" },

    { key: "applicationMethod", label: "Application Method" },

    { key: "qualificationQuestions", label: "Qualification Questions" },

    { key: "faqs", label: "FAQs" },

    { key: "caseStudies", label: "Case Studies" },

    { key: "salesInformation", label: "Sales Information" },

    { key: "media", label: "Media" },

    { key: "approval", label: "Approval" }

];


// ========================================
// INITIAL PRODUCT DATA
// ========================================

const createInitialProduct = () => ({

    productKey: "",

    identity: {
        name: "",
        code: "",
        shortDescription: "",
        detailedDescription: "",
        categories: [],
        packaging: [],
        variants: []
    },

    isActive: false,

    approval: {
        status: "draft"
    }

});


// ========================================
// PRODUCT EDITOR
// ========================================

export const AdminProductEditor = () => {

    const { admin } = useAdminAuthContext();

    const { id } = useParams();

    const navigate = useNavigate();

    const isEditMode = Boolean(id);

    const [product, setProduct] = useState(
        createInitialProduct
    );

    const [activeSection, setActiveSection] = useState(
        "identity"
    );

    const [isLoading, setIsLoading] = useState(
        isEditMode
    );

    const [isSaving, setIsSaving] = useState(false);

    const [error, setError] = useState("");

    const [successMessage, setSuccessMessage] = useState("");


    // ========================================
    // LOAD EXISTING PRODUCT
    // ========================================

    const loadProduct = useCallback(async () => {

        if (!isEditMode || !admin?.token) {
            return;
        }

        try {

            setIsLoading(true);
            setError("");

            const response = await fetch(
                API_URL.ADMIN_PRODUCT_BY_ID + id,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${admin.token}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {

                throw new Error(
                    data.error || "Failed to load product"
                );

            }

            const loadedProduct = data.product || data;

            setProduct(loadedProduct);

        } catch (err) {

            setError(err.message);

        } finally {

            setIsLoading(false);

        }

    }, [
        id,
        isEditMode,
        admin?.token
    ]);


    useEffect(() => {

        loadProduct();

    }, [loadProduct]);


    // ========================================
    // UPDATE PRODUCT SECTION
    // ========================================

    const updateProductSection = (
        sectionKey,
        sectionData
    ) => {

        setProduct((previous) => ({

            ...previous,

            [sectionKey]: sectionData

        }));

        setSuccessMessage("");

    };


    // ========================================
    // GENERATE PRODUCT KEY
    // ========================================

    const generateProductKey = (name) => {

        return name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

    };


    // ========================================
    // SAVE PRODUCT
    // ========================================

    const handleSave = async () => {

        try {

            setError("");
            setSuccessMessage("");

            const productName =
                product.identity?.name?.trim();

            if (!productName) {

                setActiveSection("identity");

                throw new Error(
                    "Please enter a product name before saving."
                );

            }

            const payload = {
                ...product
            };

            if (!isEditMode) {

                payload.productKey =
                    generateProductKey(productName);

                if (!payload.productKey) {

                    throw new Error(
                        "Please enter a valid product name."
                    );

                }

            }

            setIsSaving(true);

            const response = await fetch(
                API_URL.ADMIN_PRODUCT_SAVE,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${admin.token}`
                    },
                    body: JSON.stringify(payload)
                }
            );

            const data = await response.json();

            if (!response.ok) {

                throw new Error(
                    data.error || "Failed to save product"
                );

            }

            const savedProduct =
                data.product || data;

            if (!isEditMode) {

                const savedId = savedProduct._id;

                if (savedId) {

                    navigate(
                        `/administrator/product/edit/${savedId}`,
                        {
                            replace: true
                        }
                    );

                } else {

                    navigate(
                        "/administrator/products/manage"
                    );

                }

            } else {

                await loadProduct();

            }

            setSuccessMessage(
                "Product saved successfully as a draft."
            );

        } catch (err) {

            setError(err.message);

        } finally {

            setIsSaving(false);

        }

    };


    // ========================================
    // ACTIVE SECTION
    // ========================================

    const currentSection = PRODUCT_SECTIONS.find(
        (section) =>
            section.key === activeSection
    );


    // ========================================
    // SECTION CONTENT
    // ========================================

    const renderSection = () => {

        switch (activeSection) {

            case "identity":

                return (

                    <ProductIdentity
                        data={product.identity}
                        onChange={(updatedIdentity) =>
                            updateProductSection(
                                "identity",
                                updatedIdentity
                            )
                        }
                    />

                );

            case "customerProblems":

                return (
            
                    <ProductCustomerProblems
                        data={product.customerProblems}
                        onChange={(updatedProblems) =>
                            updateProductSection(
                                "customerProblems",
                                updatedProblems
                            )
                        }
                    />
            
                );

             case "applications":

                return (
            
                    <ProductApplications
                        data={product.applications}
                        onChange={(updatedApplications) =>
                            updateProductSection(
                                "applications",
                                updatedApplications
                            )
                        }
                    />
            
            );

            case "benefits":

                return (

                    <ProductBenefits
                        benefits={product.benefits}
                        differentiators={product.differentiators}
                        performanceClaims={product.performanceClaims}
                        onChange={(field, updatedItems) =>
                            updateProductSection(
                                field,
                                updatedItems
                            )
                        }
                    />

                );

            case "suitability":

                return (
            
                    <ProductSuitability
                        data={product.suitability}
                        onChange={(updatedSuitability) =>
                            updateProductSection(
                                "suitability",
                                updatedSuitability
                            )
                        }
                    />
            
                );

                case "technicalSpecifications":

                return (
            
                    <ProductTechnicalSpecifications
                        data={product.technicalSpecifications}
                        onChange={(updatedSpecifications) =>
                            updateProductSection(
                                "technicalSpecifications",
                                updatedSpecifications
                            )
                        }
                    />
            
                );

                case "applicationMethod":

                    return (

                        <ProductApplicationMethod
                            data={product.applicationMethod}
                            onChange={(updatedMethod) =>
                                updateProductSection(
                                    "applicationMethod",
                                    updatedMethod
                                )
                            }
                        />

                );

                case "qualificationQuestions":

                return (
            
                    <ProductQualificationQuestions
                        data={product.qualificationQuestions}
                        onChange={(updatedQuestions) =>
                            updateProductSection(
                                "qualificationQuestions",
                                updatedQuestions
                            )
                        }
                    />
            
                );
            
                case "faqs":

                return (
            
                    <ProductFAQs
                        data={product.faqs}
                        onChange={(updatedFAQs) =>
                            updateProductSection(
                                "faqs",
                                updatedFAQs
                            )
                        }
                    />
            
                );

            case "caseStudies":

                return (
            
                    <ProductCaseStudies
                        data={product.caseStudies}
                        onChange={(updatedCaseStudies) =>
                            updateProductSection(
                                "caseStudies",
                                updatedCaseStudies
                            )
                        }
                    />
            
                );

                case "salesInformation":

                return (
            
                    <ProductSalesInformation
                        data={product.salesInformation}
                        onChange={(updatedSalesInformation) =>
                            updateProductSection(
                                "salesInformation",
                                updatedSalesInformation
                            )
                        }
                    />
            
                );

                case "media":

                return (

                    <ProductMedia
                        data={product.media}
                        onChange={(updatedMedia) =>
                            updateProductSection(
                                "media",
                                updatedMedia
                            )
                        }
                    />

                );        
                
                case "approval":

                return (
                    <ProductApproval
                        product={product}
                        onUpdated={loadProduct}
                    />
                );


            default:

                return (

                    <div className="py-12 text-center">

                        <p className="text-gray-500">
                            This section will be implemented next.
                        </p>

                    </div>

                );

        }

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="min-h-screen bg-gray-100">

            {/* MAIN CONTENT */}

            <div className="w-full min-w-0 px-4 py-8 sm:px-6 lg:px-8">

                {/* HEADER */}

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">

                    <div>

                        <Link
                            to="/administrator/products/manage"
                            className="text-sm text-gray-600 hover:text-black"
                        >
                            ← Back to Products
                        </Link>

                        <h1 className="text-2xl font-bold text-gray-900 mt-3">

                            {isEditMode
                                ? "Edit Product"
                                : "Create Product"}

                        </h1>

                        <p className="text-sm text-gray-500 mt-1">

                            {product.identity?.name ||
                                "Enter product information and knowledge."}

                        </p>

                    </div>


                    <div className="flex items-center gap-3">

                        <span className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-full capitalize">

                            {product.approval?.status || "draft"}

                        </span>

                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving || isLoading}
                            className="px-5 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                        >

                            {isSaving
                                ? "Saving..."
                                : "Save Draft"}

                        </button>

                    </div>

                </div>


                {/* ERROR */}

                {error && (

                    <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                        {error}
                    </div>

                )}


                {/* SUCCESS */}

                {successMessage && (

                    <div className="mb-5 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
                        {successMessage}
                    </div>

                )}


                {/* LOADING */}

                {isLoading ? (

                    <div className="bg-white border rounded-lg p-10 text-center text-gray-500">

                        Loading product information...

                    </div>

                ) : (

                    <div className="flex flex-col lg:flex-row gap-5 items-start">

                        {/* SECTION NAVIGATION */}

                        <div className="w-full lg:w-64 flex-shrink-0 bg-white border rounded-lg overflow-hidden">

                            <div className="px-4 py-3 bg-gray-200 font-semibold text-sm">
                                Product Knowledge
                            </div>

                            <div className="p-2">

                                {PRODUCT_SECTIONS.map(
                                    (section, index) => (

                                        <button
                                            key={section.key}
                                            type="button"
                                            onClick={() =>
                                                setActiveSection(
                                                    section.key
                                                )
                                            }
                                            className={`w-full text-left px-3 py-3 rounded text-sm flex items-center gap-3 ${
                                                activeSection === section.key
                                                    ? "bg-gray-800 text-white"
                                                    : "text-gray-700 hover:bg-gray-100"
                                            }`}
                                        >

                                            <span className="text-xs opacity-70 w-5">
                                                {index + 1}.
                                            </span>

                                            <span>
                                                {section.label}
                                            </span>

                                        </button>

                                    )
                                )}

                            </div>

                        </div>


                        {/* SECTION EDITOR */}

                        <div className="flex-1 min-w-0 bg-white border rounded-lg shadow-sm">

                            <div className="px-6 py-5 border-b">

                                <h2 className="text-lg font-semibold text-gray-900">

                                    {currentSection?.label}

                                </h2>

                            </div>

                            <div className="p-6">

                                {renderSection()}

                            </div>

                        </div>

                    </div>

                )}

            </div>

        </div>

    );

};

export default AdminProductEditor;
